// De schil van elk scherm, zoals in het prototype: zijbalk per rol, bovenbalk met klantkeuze en Nieuwe taak,
// en het gebruikersmenu. De rol komt uit Entra (plannersgroep); een medewerker ziet alleen de eigen schermen.
// Met Clockify toont de bovenbalk hoeveel collega's nu een timer hebben lopen (met de lijst van het hele team) en
// wanneer de timers laatst gelezen zijn; zonder Clockify blijft dat weg.
import { useMemo, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api, markLoggedOut } from "../lib/api";
import { useCapacity, useClockifyLive, type ClockifyStatus } from "../lib/api-clockify";
import { catOf } from "../lib/board";
import { when } from "../lib/cleanup";
import { absentText, klantHealth, teamNow, timerText, type KlantHealth, type TeamKind, type TeamRow } from "../lib/health";
import { useMe, usePlanning, useProposals } from "../lib/hooks";
import { Icon, type IconName } from "../lib/icons";
import { ddmm, deadline, plannerPlanUrl, todayIn } from "../lib/planning";
import { rightsOf, useSettings } from "../lib/settings";
import type { Me, PlanningData } from "../lib/types";
import { FaseDrawerHost } from "../components/FaseDrawer";
import { NewTaskDrawer } from "../components/NewTaskDrawer";
import { Av } from "../components/Avatar";
import { useAppState } from "./AppState";

interface NavItem { to: string; icon: IconName; label: string }
const PLANNER_NAV: [string, NavItem[]][] = [
  ["Werken", [
    { to: "/mijn-werk", icon: "mijnwerk", label: "Mijn werk" },
    { to: "/planning", icon: "planning", label: "Planning" },
    { to: "/taken", icon: "checklist", label: "Taken" },
    { to: "/opschonen", icon: "opschonen", label: "Opschonen" },
  ]],
  ["Inzicht", [
    { to: "/klantprojecten", icon: "klanten", label: "Klantprojecten" },
    { to: "/support", icon: "support", label: "Support" },
  ]],
  ["Beheer", [
    { to: "/teams", icon: "users", label: "Teams" },
    { to: "/koppelingen", icon: "link", label: "Koppelingen" },
    { to: "/instellingen", icon: "sliders", label: "Instellingen" },
  ]],
];
const MEDEWERKER_NAV: [string, NavItem[]][] = [
  ["Werken", [
    { to: "/mijn-werk", icon: "mijnwerk", label: "Mijn werk" },
    { to: "/planning", icon: "planning", label: "Planning" },
    { to: "/taken", icon: "checklist", label: "Taken" },
  ]],
  ["Inzicht", [
    { to: "/klantprojecten", icon: "klanten", label: "Klantprojecten" },
    { to: "/support", icon: "support", label: "Support" },
  ]],
];

const roleLabel = (me: Me) => (me.role === "planner" ? (me.superadmin ? "Planner en superadmin" : "Planner") : "Medewerker");

function UserMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const [out, setOut] = useState(false);
  const qc = useQueryClient();
  const signOut = async () => {
    markLoggedOut();
    await qc.cancelQueries();
    await api.logout().catch(() => {});
    qc.clear();
    setOut(true);
  };
  if (out) {
    return (
      <div className="p-out" role="dialog" aria-modal="true" aria-labelledby="po">
        <div className="p-out-c">
          <span className="p-out-w">Planning</span><span className="p-out-by">by TLCC</span>
          <h2 id="po">U bent afgemeld</h2>
          <p>Meld u opnieuw aan met uw TLCC-account om verder te werken.</p>
          <a className="ex-btn ex-btn--primary" href="/auth/login">Aanmelden met Microsoft</a>
        </div>
      </div>
    );
  }
  return (
    <div className="ex-sidebar-foot p-umwrap">
      <button type="button" className="ex-user" aria-haspopup="menu" aria-expanded={open} aria-label="Gebruikersmenu" onClick={() => setOpen(!open)}>
        <Av id={me.id} init={me.initials} cls="ex-avatar" />
        <span style={{ minWidth: 0 }}><p>{roleLabel(me)}</p><p>{me.name}</p></span>
      </button>
      {open && (
        <>
          <button type="button" className="p-dd-scrim" aria-label="Sluiten" onClick={() => setOpen(false)} />
          <div className="p-um" role="menu" aria-label="Gebruikersmenu">
            <div className="p-um-h"><Av id={me.id} init={me.initials} cls="ex-avatar" /><span className="t"><b>{me.name}</b><small>{roleLabel(me)} · TLCC</small></span></div>
            <div className="p-um-s">
              <button type="button" className="p-um-i" onClick={signOut}>
                <span className="p-um-ic"><Icon name="ext" size={14} /></span>
                <span className="t"><b>Afmelden</b><small>Van de planning, niet van Microsoft 365</small></span>
              </button>
            </div>
            {me.dev && <p className="p-um-f">Lokaal aangemeld met het az CLI-account.</p>}
          </div>
        </>
      )}
    </div>
  );
}

function KlantPicker() {
  const { data } = usePlanning();
  const { klant, setKlant } = useAppState();
  const [open, setOpen] = useState(false);
  const cur = data?.klanten.find((k) => k.id === klant);
  const counts = new Map<string, number>();
  for (const t of data?.tasks || []) counts.set(t.projectId, (counts.get(t.projectId) || 0) + 1);
  // de stip is de status uit Klantprojecten
  const health = useMemo(() => new Map(data ? data.klanten.map((k) => [k.id, klantHealth(k, data)] as const) : []), [data]);
  const dot = (h?: KlantHealth) => <span className={"ex-dot ex-dot--" + (h ? h.tone : "ok")} title={h?.status} />;
  const items = [{ id: null as string | null, label: "Alle klanten", sub: (data?.klanten.length || 0) + " klantplannen" }].concat(
    (data?.klanten || []).map((k) => ({ id: k.id as string | null, label: k.name, sub: (counts.get(k.id) || 0) + " open taken" })),
  );
  return (
    <div className="ex-dd p-kdd">
      <button type="button" className="ex-dd-trigger ex-dd-trigger--instance" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(!open)}>
        {dot(cur && health.get(cur.id))}<b>{cur ? cur.name : "Alle klanten"}</b><Icon name="down" size={16} />
      </button>
      {open && (
        <>
          <button type="button" className="p-dd-scrim" aria-label="Sluiten" onClick={() => setOpen(false)} />
          <div className="p-pop p-pop-s p-pop-k p-kpop" role="listbox" aria-label="Klant kiezen">
            <div className="p-pop-h"><span>Klant op elk scherm</span></div>
            <div className="p-pop-b">
              {items.map((o) => (
                <button key={o.id || "alle"} type="button" className={"p-opt1 p-kopt" + (klant === o.id ? " on" : "")} onClick={() => { setKlant(o.id); setOpen(false); }}>
                  {dot(o.id ? health.get(o.id) : undefined)}<span className="nm">{o.label}</span><span className="c">{o.sub}</span>
                </button>
              ))}
            </div>
            <div className="p-pop-f">Blijft staan als u van scherm wisselt</div>
          </div>
        </>
      )}
    </div>
  );
}

/** Aantal bij Opschonen: af te sluiten, deadline meer dan 30 dagen verlopen, en voorstellen die op de planner wachten. */
function OpschonenCount() {
  const { data } = usePlanning();
  const { data: proposals } = useProposals();
  if (!data) return null;
  const open = new Set(data.tasks.map((t) => t.id));
  const n = data.tasks.filter((t) => { const c = catOf(t); return c === "afsluiten" || (c === "actief" && deadline(t.end, data.today).key === "verlopen"); }).length
    + (proposals || []).filter((p) => open.has(p.taskId)).length;
  return n ? <span className="p-navcount">{n}</span> : null;
}

const ST: Record<TeamKind, string> = { bezig: "bezig", vergeten: "laatst", laatst: "laatst", afwezig: "afwezig", niet: "laatst" };
const ORDER: Record<TeamKind, number> = { bezig: 0, vergeten: 1, laatst: 2, afwezig: 3, niet: 4 };

/** Eén regel in Wie is nu bezig: waaraan, en rechts sinds wanneer of wanneer laatst gelogd. */
function busyLine(r: TeamRow, data: PlanningData): { text: string; at: string; cls: string } {
  const task = r.timer?.taskId ? data.tasks.find((t) => t.id === r.timer!.taskId) : null;
  const last = r.last ? when(r.last, data.today) : "";
  if (r.kind === "bezig") return { text: timerText(r.timer!, task), at: "sinds " + when(r.timer!.since, data.today).replace(/^vandaag /, ""), cls: " t-ok" };
  if (r.kind === "vergeten") return { text: "Timer loopt sinds " + ddmm(todayIn("Europe/Brussels", new Date(r.timer!.since))) + ", vergeten?", at: last, cls: " t-warn" };
  if (r.kind === "afwezig") return { text: absentText(r.absent!, data.today), at: "afwezig", cls: "" };
  if (r.kind === "niet") return { text: "Niet gevonden in Clockify", at: "", cls: "" };
  return { text: last ? "Laatst gelogd" : "Nog niets gelogd", at: last, cls: "" };
}

/** "4 collega's nu bezig", met de lijst van het hele team. Wie vandaag afwezig is, komt uit de capaciteit: die laadt
 *  pas als de lijst opengaat (en enkel als de gebruiker de capaciteit mag zien). */
function BusyButton() {
  const { data: live } = useClockifyLive();
  const { data } = usePlanning();
  const { data: me } = useMe();
  const { data: settings } = useSettings();
  const [open, setOpen] = useState(false);
  const mayCap = me?.role === "planner" || rightsOf(settings?.settings).seeCapacity;
  const { data: cap } = useCapacity(open && mayCap);
  if (!live || !data || live.clockify.state === "uit") return null;
  const rows = teamNow(data, live, mayCap ? cap : null).sort((a, b) => ORDER[a.kind] - ORDER[b.kind]
    || (a.kind === "bezig" ? a.timer!.since.localeCompare(b.timer!.since) : (b.last || "").localeCompare(a.last || "")) || a.p.name.localeCompare(b.p.name));
  const n = rows.filter((r) => r.kind === "bezig").length, stale = rows.filter((r) => r.kind === "vergeten").length;
  const foot = (n === 1 ? "1 timer loopt" : n + " timers lopen") + " in Clockify" + (stale ? " · " + stale + " wellicht vergeten" : "") + " · de rest laatst actief";
  return (
    <div className="p-ddwrap p-busywrap">
      <button type="button" className={"ex-live p-busybtn" + (n ? "" : " idle")} aria-haspopup="true" aria-expanded={open} onClick={() => setOpen(!open)}>
        {n ? n + (n === 1 ? " collega nu bezig" : " collega's nu bezig") : "Niemand nu bezig"}
      </button>
      {open && (
        <>
          <button type="button" className="p-dd-scrim" aria-label="Sluiten" onClick={() => setOpen(false)} />
          <div className="p-pop p-busypop" role="dialog" aria-label="Wie is nu bezig">
            <div className="p-pop-h"><span>Wie is nu bezig</span></div>
            <div className="p-pop-b">
              {rows.map((r) => {
                const l = busyLine(r, data);
                return (
                  <div key={r.p.id} className="p-busyi">
                    <span className="p-avwrap"><Av id={r.p.id} init={r.p.initials} cls="p-av s" /><span className={"st " + ST[r.kind]} /></span>
                    <span className="t"><b>{r.p.name}</b><span title={l.text}>{l.text}</span></span>
                    <span className={"p-busy-w" + l.cls}>{l.at}</span>
                  </div>
                );
              })}
            </div>
            <div className="p-pop-f">{foot}</div>
          </div>
        </>
      )}
    </div>
  );
}

/** "Clockify 1 min geleden": wanneer de lopende timers laatst gelezen zijn (elke minuut). Zonder sleutel niets. */
function clockifySync(c: ClockifyStatus | undefined, today: string): { text: string; cls: string; title?: string } | null {
  if (!c || c.state === "uit") return null;
  if (c.state === "wacht") return { text: "Clockify: eerste ronde loopt", cls: "" };
  const at = c.timersAt || c.lastOk;
  const min = at ? Math.floor((Date.now() - Date.parse(at)) / 60e3) : null;
  if (c.state === "fout") return { text: "Clockify niet bijgewerkt" + (at ? " sinds " + when(at, today).replace(/^vandaag /, "") : ""), cls: "t-warn", title: c.error || undefined };
  if (min == null) return { text: "Clockify", cls: "" };
  return { text: min < 1 ? "Clockify zojuist" : min < 60 ? "Clockify " + min + " min geleden" : "Clockify " + when(at!, today), cls: min >= 10 ? "t-warn" : "" };
}

function Topbar({ withKlant = true }: { withKlant?: boolean }) {
  const { sideMin, toggleSide, klant, setNewTaskOpen } = useAppState();
  const { data: me } = useMe();
  const { data, dataUpdatedAt } = usePlanning();
  const { data: live } = useClockifyLive();
  const at = data ? new Date(dataUpdatedAt).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" }) : "…";
  const cs = clockifySync(live?.clockify, data?.today || todayIn());
  return (
    <header className="ex-topbar p-topbar">
      <div className="ex-topbar-left">
        <button type="button" className="ex-toggle" aria-label={sideMin ? "Menu uitklappen" : "Menu inklappen"} title={sideMin ? "Menu uitklappen" : "Menu inklappen"} aria-pressed={sideMin} onClick={toggleSide}>
          <Icon name="side" size={18} />
        </button>
        {withKlant && <KlantPicker />}
      </div>
      <div className="p-topbar-r">
        <BusyButton />
        <span className="p-sync">Planner · bijgewerkt {at}{cs && <> · <span className={cs.cls} title={cs.title}>{cs.text}</span></>}</span>
        {me?.canWrite
          ? <button type="button" className="ex-btn ex-btn--primary" onClick={() => setNewTaskOpen(true)}><Icon name="plus" size={15} />Nieuwe taak</button>
          : me && <a className="ex-btn ex-btn--primary" href={plannerPlanUrl(me, klant)} target="_blank" rel="noreferrer" title="Taken maken doet u in Planner"><Icon name="plus" size={15} />Nieuwe taak in Planner</a>}
      </div>
    </header>
  );
}

/** Iets kon niet laden (Planner of de server is even niet bereikbaar): zeggen, en opnieuw laten proberen. */
export function LoadError({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <div className="p-loading p-loaderr">
      <p>{what} kon niet laden. Probeer het over een ogenblik opnieuw.</p>
      <button type="button" className="ex-btn" onClick={onRetry}>Opnieuw proberen</button>
    </div>
  );
}

export function Layout({ children }: { children?: ReactNode }) {
  const { data: me, error, refetch } = useMe();
  const { sideMin, newTaskOpen } = useAppState();
  const { pathname } = useLocation();
  if (!me && error) return <LoadError what="De planning" onRetry={() => refetch()} />;
  if (!me) return <div className="p-loading">Even geduld…</div>;
  const nav = (me.role === "planner" ? PLANNER_NAV : MEDEWERKER_NAV).map(([title, items]) => [title, items.filter((i) => i.to !== "/instellingen" || me.superadmin)] as const);
  return (
    <div className={"p-app" + (sideMin ? " side-min" : "")}>
      <aside className="ex-sidebar p-sidebar">
        <div className="ex-sidebar-logo"><span className="ex-wordmark">Planning</span><span className="ex-by">by TLCC</span></div>
        <nav className="ex-nav">
          {nav.map(([title, items]) => (
            <div key={title} className="ex-nav-section">
              <p>{title}</p>
              {items.map((i) => (
                <NavLink key={i.to} to={i.to} className="ex-nav-item" title={i.label}>
                  <Icon name={i.icon} size={17} /><span>{i.label}</span>{i.to === "/opschonen" && <OpschonenCount />}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <UserMenu me={me} />
      </aside>
      <div className="p-main">
        <Topbar withKlant={!/^\/(instellingen|teams|koppelingen)/.test(pathname)} />
        <main className="p-content">{children ?? <Outlet />}</main>
      </div>
      {!/^\/taken/.test(pathname) && <FaseDrawerHost />}{/* in Taken opent ?fase= het fasevenster */}
      {newTaskOpen && me.canWrite && <NewTaskDrawer />}
    </div>
  );
}
